// uploadCarImage.ts
const bucket = import.meta.env.VITE_GCS_BUCKET || 'onwheels-media';

export async function uploadCarImage({
  file,
  userId,
  entityId,
  type,
  accessToken,
}: {
  file: File;
  userId: string;
  entityId: string;
  type: 'car' | 'symptom' | 'repair' | 'reminder' | 'profile' | 'octa' | 'kasko';
  accessToken: string;
}): Promise<string> {
  console.log("⬆️ uploadCarImage called with:");
  console.log("📁 file:", file);
  console.log("📦 type:", type);
  console.log("🆔 entityId:", entityId);
  console.log("🔐 accessToken present:", !!accessToken); // Only log if token exists, not the content

  if (!file || !userId || !type || !entityId || !accessToken) {
    throw new Error("Missing required upload parameters");
  }

  // Sanitize filename: replace spaces and special characters that could cause signature issues
  // This matches the edge function's sanitization but we do it here to ensure consistency
  const sanitizedFileName = file.name.replace(/[^ -\x7f\w.-]/g, '_').replace(/\s+/g, '_');

  // Construct Supabase function URL (same pattern as getSignedViewUrl)
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }

  const requestBody = {
    fileName: sanitizedFileName,
    userId,
    kind: type,
    carId: (type === 'car' || type === 'octa' || type === 'kasko') ? entityId : undefined,
    symptomId: type === 'symptom' ? entityId : undefined,
    repairJobId: type === 'repair' ? entityId : undefined,
    // profile type doesn't need entityId, but we pass userId as entityId for consistency
    contentType: file.type,
  };
  
  console.log("📤 Request body to Edge Function:", JSON.stringify(requestBody, null, 2));
  
  const response = await fetch(`${supabaseUrl}/functions/v1/upload-car-image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const err = await response.text();
    console.error('Upload error:', err);
    throw new Error(`Failed to get signed URL: ${err}`);
  }

  const { signedUrl, path } = await response.json();

  const uploadResponse = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errText = await uploadResponse.text();
    console.error('GCS Upload failed:', errText);
    throw new Error('Image upload failed to GCS');
  }

  return path;
}

export async function getSignedUrlForImage(path: string): Promise<string> {
  return `https://storage.googleapis.com/${bucket}/${path}`;
}
